import React, { useCallback, useMemo } from 'react';
import {
  FileText,
  CircleDot,
  CheckSquare,
  Settings2,
  Bell,
  ShieldCheck,
  ClipboardList,
  Plus,
  Trash2,
} from 'lucide-react';
import type {
  BlueprintStage,
  BlueprintChecklistItem,
  BlueprintFormField,
  BlueprintStageRules,
  BlueprintStageNotifications,
  BlueprintExitOptions,
} from '../../../types/processBlueprint';
import type { ExecutionField } from '../../../types/card';
import { FormFieldEditor, FIELD_TYPES } from './shared/FormFieldEditor';

// ============================================================================
// PROPS
// ============================================================================

export interface BlueprintStageEditorProps {
  stage: BlueprintStage;
  onChange: (updated: BlueprintStage) => void;
  readOnly?: boolean;
  availableRoles?: string[];
}

// ============================================================================
// BRIDGE: BlueprintFormField <-> ExecutionField
// ============================================================================

/**
 * Converte BlueprintFormField[] para ExecutionField[] (para o FormFieldEditor).
 * options: string[] -> { value, label }[]
 */
function toExecutionFields(fields: BlueprintFormField[]): ExecutionField[] {
  return fields.map((f) => ({
    id: f.id,
    type: f.type,
    label: f.label,
    required: f.required ?? false,
    options: f.options?.map((opt) => ({ value: opt, label: opt })),
    defaultValue: f.defaultValue,
  }));
}

/**
 * Converte ExecutionField[] de volta para BlueprintFormField[].
 * options: { value, label }[] -> string[]
 */
function toBlueprintFields(fields: ExecutionField[]): BlueprintFormField[] {
  return fields.map((f) => ({
    id: f.id,
    type: f.type as BlueprintFormField['type'],
    label: f.label,
    required: f.required || undefined,
    options: f.options?.map((opt) => opt.value),
    defaultValue: f.defaultValue,
  }));
}

// ============================================================================
// HELPERS
// ============================================================================

function generateChecklistId(): string {
  return `checklist_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

/** Wrapper de secao com card branco + borda */
function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded p-3">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className="text-slate-500 dark:text-slate-400 shrink-0" />
        <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
          {title}
        </h4>
      </div>
      {children}
    </div>
  );
}

/** Input base reutilizavel */
function FieldInput({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
        {label}
      </label>
      {children}
    </div>
  );
}

const inputClass =
  'w-full px-2 py-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 dark:focus:ring-blue-500 text-slate-700 dark:text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-slate-50 dark:disabled:bg-slate-800';

const checkboxClass =
  'w-3.5 h-3.5 rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-400 focus:ring-1 disabled:opacity-50 disabled:cursor-not-allowed';

// ============================================================================
// COMPONENT
// ============================================================================

export const BlueprintStageEditor: React.FC<BlueprintStageEditorProps> = ({
  stage,
  onChange,
  readOnly = false,
  availableRoles = [],
}) => {
  // -- Updaters ---------------------------------------------------------------

  const update = useCallback(
    (patch: Partial<BlueprintStage>) => {
      onChange({ ...stage, ...patch });
    },
    [stage, onChange],
  );

  const updateRules = useCallback(
    (patch: Partial<BlueprintStageRules>) => {
      onChange({ ...stage, rules: { ...stage.rules, ...patch } });
    },
    [stage, onChange],
  );

  const updateNotifications = useCallback(
    (patch: Partial<BlueprintStageNotifications>) => {
      onChange({ ...stage, notifications: { ...stage.notifications, ...patch } });
    },
    [stage, onChange],
  );

  const updateExitOptions = useCallback(
    (patch: Partial<BlueprintExitOptions>) => {
      onChange({ ...stage, exitOptions: { ...stage.exitOptions, ...patch } });
    },
    [stage, onChange],
  );

  // -- Checklist handlers -----------------------------------------------------

  const addChecklistItem = useCallback(() => {
    const newItem: BlueprintChecklistItem = {
      id: generateChecklistId(),
      label: '',
      required: false,
    };
    onChange({
      ...stage,
      checklist: [...(stage.checklist ?? []), newItem],
    });
  }, [stage, onChange]);

  const updateChecklistItem = useCallback(
    (itemId: string, patch: Partial<BlueprintChecklistItem>) => {
      const updated = (stage.checklist ?? []).map((it) =>
        it.id === itemId ? { ...it, ...patch } : it,
      );
      onChange({ ...stage, checklist: updated });
    },
    [stage, onChange],
  );

  const removeChecklistItem = useCallback(
    (itemId: string) => {
      onChange({
        ...stage,
        checklist: (stage.checklist ?? []).filter((it) => it.id !== itemId),
      });
    },
    [stage, onChange],
  );

  // -- Form fields bridge -----------------------------------------------------

  const executionFields = useMemo(
    () => toExecutionFields(stage.formFields ?? []),
    [stage.formFields],
  );

  const handleFormFieldsChange = useCallback(
    (fields: ExecutionField[]) => {
      onChange({ ...stage, formFields: toBlueprintFields(fields) });
    },
    [stage, onChange],
  );

  // -- Completion type --------------------------------------------------------

  const completionType = stage.completionType ?? 'simple_complete';

  const setCompletionType = useCallback(
    (type: 'simple_complete' | 'approve_reject') => {
      const rules: BlueprintStageRules = { ...stage.rules };
      if (type === 'approve_reject') {
        rules.requireApproval = true;
      } else {
        rules.requireApproval = false;
      }
      onChange({ ...stage, completionType: type, rules });
    },
    [stage, onChange],
  );

  // -- Render -----------------------------------------------------------------

  return (
    <div className="flex flex-col gap-3">
      {/* ---- 1. Basico ---- */}
      <Section icon={ClipboardList} title="Basico">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <FieldInput label="Nome *">
              <input
                type="text"
                value={stage.name}
                onChange={(e) => update({ name: e.target.value })}
                disabled={readOnly}
                placeholder="Nome da etapa"
                className={inputClass}
                required
              />
            </FieldInput>
          </div>

          <div className="col-span-2">
            <FieldInput label="Descricao">
              <textarea
                value={stage.description ?? ''}
                onChange={(e) => update({ description: e.target.value || undefined })}
                disabled={readOnly}
                placeholder="Descricao opcional da etapa"
                rows={2}
                className={inputClass + ' resize-none'}
              />
            </FieldInput>
          </div>

          <FieldInput label="Ordem">
            <input
              type="number"
              value={stage.order}
              onChange={(e) => update({ order: parseInt(e.target.value, 10) || 0 })}
              disabled={readOnly}
              min={1}
              className={inputClass}
            />
          </FieldInput>

          <FieldInput label="Responsavel (Role)">
            {availableRoles.length > 0 ? (
              <select
                value={stage.role ?? ''}
                onChange={(e) => update({ role: e.target.value || undefined })}
                disabled={readOnly}
                className={inputClass}
              >
                <option value="">-- Selecione --</option>
                {availableRoles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={stage.role ?? ''}
                onChange={(e) => update({ role: e.target.value || undefined })}
                disabled={readOnly}
                placeholder="Ex: Analista, Gerente"
                className={inputClass}
              />
            )}
          </FieldInput>

          <FieldInput label="SLA (horas)">
            <input
              type="number"
              value={stage.slaHours ?? ''}
              onChange={(e) =>
                update({
                  slaHours: e.target.value ? parseFloat(e.target.value) : undefined,
                })
              }
              disabled={readOnly}
              min={0}
              step={0.5}
              placeholder="0"
              className={inputClass}
            />
          </FieldInput>
        </div>
      </Section>

      {/* ---- 2. Instrucao de Trabalho (SOP) ---- */}
      <Section icon={FileText} title="Instrucao de Trabalho (SOP)">
        <textarea
          value={stage.instruction ?? ''}
          onChange={(e) => update({ instruction: e.target.value || undefined })}
          disabled={readOnly}
          placeholder="Descreva o passo-a-passo que o operador deve seguir nesta etapa..."
          rows={4}
          className={inputClass + ' resize-y'}
        />
      </Section>

      {/* ---- 3. Tipo de Conclusao ---- */}
      <Section icon={CircleDot} title="Tipo de Conclusao">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setCompletionType('simple_complete')}
            disabled={readOnly}
            className={`flex-1 px-3 py-2 text-xs font-semibold rounded border transition-colors ${
              completionType === 'simple_complete'
                ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-500'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Concluir
          </button>
          <button
            type="button"
            onClick={() => setCompletionType('approve_reject')}
            disabled={readOnly}
            className={`flex-1 px-3 py-2 text-xs font-semibold rounded border transition-colors ${
              completionType === 'approve_reject'
                ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-500'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            Aprovar / Rejeitar
          </button>
        </div>
        {completionType === 'approve_reject' && (
          <p className="mt-2 text-[10px] text-amber-600 dark:text-amber-400">
            O operador precisara aprovar ou rejeitar esta etapa para avancar.
          </p>
        )}
      </Section>

      {/* ---- 4. Campos de Formulario ---- */}
      <FormFieldEditor
        fields={executionFields}
        onChange={handleFormFieldsChange}
        disabled={readOnly}
      />

      {/* ---- 5. Checklist ---- */}
      <Section icon={CheckSquare} title="Checklist">
        {(stage.checklist ?? []).length === 0 ? (
          <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-3">
            Nenhum item de checklist adicionado.
          </p>
        ) : (
          <div className="space-y-2 mb-3">
            {(stage.checklist ?? []).map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-600 group"
              >
                <input
                  type="text"
                  value={item.label}
                  onChange={(e) =>
                    updateChecklistItem(item.id, { label: e.target.value })
                  }
                  disabled={readOnly}
                  placeholder="Item do checklist"
                  className="flex-1 min-w-0 px-2 py-1 text-xs bg-transparent border-none focus:outline-none focus:ring-0 text-slate-700 dark:text-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                />

                <button
                  type="button"
                  onClick={() =>
                    updateChecklistItem(item.id, { required: !item.required })
                  }
                  disabled={readOnly}
                  className={`px-1.5 py-0.5 text-[9px] font-bold rounded transition-colors shrink-0 ${
                    item.required
                      ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                      : 'bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500'
                  } ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {item.required ? 'REQ' : 'OPC'}
                </button>

                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => removeChecklistItem(item.id)}
                    className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded opacity-0 group-hover:opacity-100 transition-all shrink-0"
                    title="Remover item"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {!readOnly && (
          <button
            type="button"
            onClick={addChecklistItem}
            className="flex items-center gap-1 px-2 py-1.5 text-[10px] font-bold text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded border border-blue-200 dark:border-blue-800 transition-colors w-full justify-center"
          >
            <Plus size={12} />
            Adicionar item
          </button>
        )}
      </Section>

      {/* ---- 6. Opcoes de Saida ---- */}
      <Section icon={ShieldCheck} title="Opcoes de Saida">
        <div className="flex flex-col gap-2.5">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stage.exitOptions?.requireAttachment ?? false}
              onChange={(e) =>
                updateExitOptions({ requireAttachment: e.target.checked })
              }
              disabled={readOnly}
              className={checkboxClass}
            />
            <span className="text-xs text-slate-700 dark:text-slate-300">
              Exige anexo obrigatorio
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stage.exitOptions?.requireJustification ?? false}
              onChange={(e) =>
                updateExitOptions({ requireJustification: e.target.checked })
              }
              disabled={readOnly}
              className={checkboxClass}
            />
            <span className="text-xs text-slate-700 dark:text-slate-300">
              Exige observacao/justificativa
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stage.exitOptions?.requirePhoto ?? false}
              onChange={(e) =>
                updateExitOptions({ requirePhoto: e.target.checked })
              }
              disabled={readOnly}
              className={checkboxClass}
            />
            <span className="text-xs text-slate-700 dark:text-slate-300">
              Exige foto (mobile)
            </span>
          </label>
        </div>
      </Section>

      {/* ---- 7. Regras de Transicao ---- */}
      <Section icon={Settings2} title="Regras de Transicao">
        <div className="flex flex-col gap-2.5">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stage.rules?.canSkip ?? false}
              onChange={(e) => updateRules({ canSkip: e.target.checked })}
              disabled={readOnly}
              className={checkboxClass}
            />
            <span className="text-xs text-slate-700 dark:text-slate-300">
              Pode ser pulada
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stage.rules?.canRevert ?? false}
              onChange={(e) => updateRules({ canRevert: e.target.checked })}
              disabled={readOnly}
              className={checkboxClass}
            />
            <span className="text-xs text-slate-700 dark:text-slate-300">
              Pode ser revertida
            </span>
          </label>

          <FieldInput label="Condicao customizada">
            <input
              type="text"
              value={stage.rules?.condition ?? ''}
              onChange={(e) =>
                updateRules({ condition: e.target.value || undefined })
              }
              disabled={readOnly}
              placeholder="Ex: stage.previous.approved === true"
              className={inputClass}
            />
          </FieldInput>
        </div>
      </Section>

      {/* ---- 8. Notificacoes ---- */}
      <Section icon={Bell} title="Notificacoes">
        <div className="flex flex-col gap-2.5">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stage.notifications?.onStart ?? false}
              onChange={(e) =>
                updateNotifications({ onStart: e.target.checked })
              }
              disabled={readOnly}
              className={checkboxClass}
            />
            <span className="text-xs text-slate-700 dark:text-slate-300">
              Notificar ao iniciar
            </span>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stage.notifications?.onComplete ?? false}
              onChange={(e) =>
                updateNotifications({ onComplete: e.target.checked })
              }
              disabled={readOnly}
              className={checkboxClass}
            />
            <span className="text-xs text-slate-700 dark:text-slate-300">
              Notificar ao completar
            </span>
          </label>

          <FieldInput label="Alertar quando SLA atingir (%)">
            <input
              type="number"
              value={stage.notifications?.onSlaWarningPercent ?? ''}
              onChange={(e) =>
                updateNotifications({
                  onSlaWarningPercent: e.target.value
                    ? parseInt(e.target.value, 10)
                    : undefined,
                })
              }
              disabled={readOnly}
              min={0}
              max={100}
              placeholder="Ex: 80"
              className={inputClass}
            />
          </FieldInput>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stage.notifications?.onSlaBreached ?? false}
              onChange={(e) =>
                updateNotifications({ onSlaBreached: e.target.checked })
              }
              disabled={readOnly}
              className={checkboxClass}
            />
            <span className="text-xs text-slate-700 dark:text-slate-300">
              Notificar quando SLA estourar
            </span>
          </label>
        </div>
      </Section>
    </div>
  );
};

export default BlueprintStageEditor;
